import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { useGoogleAnalytics, usePageTracking } from "@/hooks/useGoogleAnalytics";
import { useCrispChat } from "@/hooks/useCrispChat";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { organizationSchema, websiteSchema } from "@/lib/schema";
import "@/index.css";

const queryClient = new QueryClient();

function Analytics({ enabled }: { enabled: boolean }) {
  useGoogleAnalytics(enabled);
  usePageTracking(enabled);
  return null;
}

function CrispChat({ enabled }: { enabled: boolean }) {
  useCrispChat(enabled);
  return null;
}


export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Enno & Jens | Agentur Wernigerode – Webdesign & SEO" },
      {
        name: "description",
        content:
          "Agentur aus Wernigerode im Harz für Webdesign, SEO, GEO, KI-Beratung und Digitalisierung. Persönlich, herstellerunabhängig und DSGVO-konform.",
      },
      { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" },
      { name: "author", content: "Goldhammer & Kerwel GbR" },
      {
        name: "google-site-verification",
        content: "qqqyOhQ2o4WJL5EWG5vlbXpT844Jlc7NwO3SBDNxAFk",
      },
      { name: "theme-color", content: "#252B37" },
      {
        property: "og:title",
        content: "Enno & Jens | Webdesign Wernigerode – SEO optimierte Webseiten",
      },
      {
        property: "og:description",
        content:
          "Agentur aus Wernigerode im Harz für Webdesign, SEO, GEO, KI-Beratung und Digitalisierung. Persönlich, herstellerunabhängig und DSGVO-konform.",
      },
      { property: "og:url", content: "https://www.enno-und-jens.de/" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Enno & Jens" },
      { property: "og:locale", content: "de_DE" },
      {
        property: "og:image",
        content: "https://www.enno-und-jens.de/images/hero-bg-1280.webp",
      },
      { property: "og:image:width", content: "1280" },
      { property: "og:image:height", content: "720" },
      {
        property: "og:image:alt",
        content: "Agentur Enno & Jens aus Wernigerode im Harz",
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Enno & Jens | Webdesign Wernigerode – SEO optimierte Webseiten",
      },
      {
        name: "twitter:description",
        content:
          "Agentur aus Wernigerode im Harz für Webdesign, SEO, GEO, KI-Beratung und Digitalisierung. Persönlich, herstellerunabhängig und DSGVO-konform.",
      },
      {
        name: "twitter:image",
        content: "https://www.enno-und-jens.de/images/hero-bg-1280.webp",
      },
    ],
    links: [
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      { rel: "alternate", type: "text/plain", href: "/llms.txt", title: "LLMs.txt" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Enno & Jens – Ratgeber",
        href: "/rss.xml",
      },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.webp" },
      {
        rel: "icon",
        type: "image/webp",
        sizes: "32x32",
        href: "/favicon-32x32.webp",
      },
      {
        rel: "icon",
        type: "image/webp",
        sizes: "16x16",
        href: "/favicon-16x16.webp",
      },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      {
        src: "https://analytics.ahrefs.com/analytics.js",
        "data-key": "vwTD7CeqvEnTfiisJDg/7A",
        async: true,
      },
      organizationSchema(),
      websiteSchema(),
    ],
  }),
  errorComponent: RootErrorComponent,
  notFoundComponent: () => (
    <RootDocument>
      <div style={{ padding: 32 }}>
        <h1>404 – Seite nicht gefunden</h1>
        <p>
          <a href="/">Zur Startseite</a>
        </p>
      </div>
    </RootDocument>
  ),
  component: RootComponent,
});

function RootComponent() {
  const consent = useCookieConsent();
  const analyticsEnabled = consent === "accepted";
  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Analytics enabled={analyticsEnabled} />
          <CrispChat enabled={analyticsEnabled} />
          <ScrollToTop />
          <Outlet />
          <CookieBanner />
        </TooltipProvider>
      </QueryClientProvider>
    </RootDocument>
  );
}



function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        <div id="root">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

function RootErrorComponent({ error }: ErrorComponentProps) {
  console.error(error);
  const [_, setTick] = useState(0);
  useEffect(() => {
    try {
      reportLovableError(error, { boundary: "tanstack_root_error_component" });
    } catch {
      /* reporter is best-effort */
    }
  }, [error]);
  return (
    <RootDocument>
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: "1.75rem", marginBottom: 12 }}>
            Etwas ist schiefgelaufen.
          </h1>
          <p style={{ lineHeight: 1.6, marginBottom: 24 }}>
            Wir konnten diese Seite nicht laden. Bitte versuchen Sie es erneut.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={() => setTick((t) => t + 1)}
              style={{
                padding: "12px 20px",
                borderRadius: 12,
                border: "none",
                background: "#f97316",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Erneut versuchen
            </button>
            <a
              href="/"
              style={{
                padding: "12px 20px",
                borderRadius: 12,
                background: "#e5e7eb",
                color: "#1f2937",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              Zur Startseite
            </a>
          </div>
        </div>
      </div>
    </RootDocument>
  );
}
